import React, { useState, useEffect } from "react";
import LayoutHR from "layouts/layout-hr";
import { IconFileSpreadsheet, IconUsers, IconListDetails, IconUserEdit } from "@tabler/icons-react";
import { useSearchParams } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { DepartmentsProvider } from "pages/hr/master/context/DepartmentsContext";
import { useUser } from "contexts/v1/masterData/UserContext";
import StaffManagementTab from "./components/staff-management-tab";
import StaffDetailTab from "./components/staff-detail-tab";

const getDepartmentName = (user) =>
  user?.department?.name ||
  user?.department_name ||
  user?.position?.department?.name ||
  "Tanpa Departemen";

const getValue = (user, keys) => {
  for (const key of keys) {
    if (user?.[key]) return user[key];
  }
  return "";
};

const formatStatus = (value) => (Number(value || 0) > 0 ? "Registered" : "Not Registered");

const StaffHubPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultTab = searchParams.get("tab") || "management";
  const [activeTab, setActiveTab] = useState(defaultTab);
  const { users } = useUser();

  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  const handleExportExcel = () => {
    const staff = users.data || [];

    if (!staff.length) {
      return;
    }

    const managementData = staff.map((user, index) => ({
      No: index + 1,
      PIN: user.fingerspot_pin || "-",
      User: user.name || "-",
      Job: user.job || "-",
      Department: getDepartmentName(user),
      Position: user.position?.name || "-",
      "Day Off": user.day_off || "-",
      Email: user.email || "-",
      Phone: user.phone || "-",
      Rekening: user.nomor_rekening || "-",
      Finger: formatStatus(user.fingerspot_finger),
      Face: formatStatus(user.fingerspot_face),
      Vein: formatStatus(user.fingerspot_vein),
    }));

    const detailsData = staff.map((user, index) => ({
      No: index + 1,
      Nama: user.name || "-",
      "L/P": getValue(user, ["gender", "sex"]) || "-",
      NIK: getValue(user, ["nik", "identity_number", "id_number", "ktp_number"]) || "-",
      "Tempat Lahir": getValue(user, ["birth_place", "place_of_birth", "tempat_lahir"]) || "-",
      "Tanggal Lahir": String(getValue(user, ["birth_date", "date_of_birth", "tgl_lahir"]) || "-").slice(0, 10),
      Alamat: getValue(user, ["address", "alamat", "domicile_address"]) || "-",
      "No Tlp": getValue(user, ["phone", "phone_number", "mobile"]) || "-",
      Departemen: getDepartmentName(user),
      "Tgl Mulai Bekerja": String(getValue(user, ["work_start_date", "start_work_date", "join_date", "joined_at"]) || "-").slice(0, 10),
      Status: getValue(user, ["employee_status", "employment_status", "status"]) || "-",
      "No Rekening": getValue(user, ["nomor_rekening", "bank_account_number", "account_number"]) || "-",
      "Catatan / Pelanggaran": getValue(user, ["staff_note"]) || "-",
    }));

    const workbook = XLSX.utils.book_new();
    const managementSheet = XLSX.utils.json_to_sheet(managementData);
    const detailsSheet = XLSX.utils.json_to_sheet(detailsData);

    managementSheet["!cols"] = [
      { wch: 6 }, { wch: 12 }, { wch: 28 }, { wch: 18 }, { wch: 22 },
      { wch: 22 }, { wch: 12 }, { wch: 28 }, { wch: 18 }, { wch: 20 },
      { wch: 16 }, { wch: 16 }, { wch: 16 },
    ];
    detailsSheet["!cols"] = [
      { wch: 6 }, { wch: 28 }, { wch: 8 }, { wch: 20 }, { wch: 18 },
      { wch: 16 }, { wch: 34 }, { wch: 18 }, { wch: 22 }, { wch: 18 },
      { wch: 16 }, { wch: 20 }, { wch: 36 },
    ];

    XLSX.utils.book_append_sheet(workbook, managementSheet, "Staff Management");
    XLSX.utils.book_append_sheet(workbook, detailsSheet, "Staff Details");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" });
    saveAs(data, `Staff-Management-Details-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <LayoutHR>
      <div className="p-3">
        {/* Page Header */}
        <div className="row align-items-center mb-4 g-3">
          <div className="col">
            <div className="text-muted small fw-bold text-uppercase mb-1">HR Management</div>
            <h1 className="m-0 fw-bold h2 d-flex align-items-center gap-2">
              <IconUsers size={28} className="text-primary" />
              Staff Management & Details
            </h1>
            <div className="text-muted mt-1">Kelola data staff, sinkronisasi Fingerspot, dan struktur departemen.</div>
          </div>
          <div className="col-auto">
            <button
              type="button"
              className="btn btn-success d-flex align-items-center gap-2"
              onClick={handleExportExcel}
              disabled={users.loading || !users.data?.length}
            >
              <IconFileSpreadsheet size={18} />
              Export Excel
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="card shadow-sm border-0 mb-4">
          <div className="card-header p-0 border-bottom-0">
            <ul className="nav nav-tabs nav-fill w-100 m-0 border-bottom-0" style={{ borderTopLeftRadius: "var(--tblr-border-radius)", borderTopRightRadius: "var(--tblr-border-radius)", overflow: "hidden" }}>
              <li className="nav-item">
                <button
                  className={`nav-link w-100 py-3 fw-medium d-flex align-items-center justify-content-center gap-2 border-0 ${activeTab === "management" ? "active bg-primary text-white" : "bg-light text-muted"}`}
                  onClick={() => handleTabChange("management")}
                  style={{ borderRadius: 0, transition: "all 0.2s ease" }}
                >
                  <IconUserEdit size={20} />
                  Staff Management
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link w-100 py-3 fw-medium d-flex align-items-center justify-content-center gap-2 border-0 ${activeTab === "details" ? "active bg-primary text-white" : "bg-light text-muted"}`}
                  onClick={() => handleTabChange("details")}
                  style={{ borderRadius: 0, transition: "all 0.2s ease" }}
                >
                  <IconListDetails size={20} />
                  Staff Details
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Tab Content */}
        <DepartmentsProvider>
          <div>
            {activeTab === "management" && <StaffManagementTab />}
            {activeTab === "details" && <StaffDetailTab />}
          </div>
        </DepartmentsProvider>
      </div>
    </LayoutHR>
  );
};

export default StaffHubPage;
